import { FC, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Organization } from "@models/organization.model.ts";
import { getDescendantIds } from "./orgHierarchy";

type Mode = "block" | "reparent" | "cascade";

interface Props {
  show: boolean;
  org: Organization | null;
  allOrgs: Organization[];
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (mode: Mode) => Promise<void>;
}

const OrgDeleteModal: FC<Props> = ({ show, org, allOrgs, isLoading, onClose, onConfirm }) => {
  const descendantCount = useMemo(
    () => (org ? getDescendantIds(org.id, allOrgs).size : 0),
    [org, allOrgs]
  );
  const hasChildren = descendantCount > 0;

  const [mode, setMode] = useState<"reparent" | "cascade">("reparent");

  if (!org) return null;

  const parentName = org.parent_id != null
    ? allOrgs.find((o) => o.id === org.parent_id)?.name ?? "top-level"
    : "top-level";

  const handleConfirm = () => onConfirm(hasChildren ? mode : "block");

  return (
    <Modal show={show} onHide={onClose} centered backdrop={isLoading ? "static" : true}>
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title as="h5">
          <i className="bi bi-exclamation-triangle-fill text-danger me-2" />
          Delete {org.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hasChildren && (
          <p className="mb-0">
            Delete <span className="fw-semibold">{org.name}</span>? This action can be undone by a platform admin.
          </p>
        )}

        {hasChildren && (
          <>
            <div className="alert alert-warning d-flex align-items-start gap-2 mb-3 py-2 px-3">
              <i className="bi bi-info-circle-fill mt-1" />
              <div>
                <div>
                  <span className="fw-semibold">{org.name}</span> has{" "}
                  <span className="fw-semibold">{descendantCount}</span> sub-organization
                  {descendantCount === 1 ? "" : "s"}. Choose how to handle them.
                </div>
              </div>
            </div>

            <Form>
              <Form.Check
                type="radio"
                id="delete-mode-reparent"
                name="delete-mode"
                checked={mode === "reparent"}
                onChange={() => setMode("reparent")}
                label={
                  <span>
                    <span className="fw-semibold">Promote sub-orgs and delete</span>
                    <div className="text-muted small">
                      Direct children become children of{" "}
                      <span className="fw-semibold">{parentName}</span>. Deeper descendants stay attached to their current parents.
                    </div>
                  </span>
                }
                className="mb-3"
              />
              <Form.Check
                type="radio"
                id="delete-mode-cascade"
                name="delete-mode"
                checked={mode === "cascade"}
                onChange={() => setMode("cascade")}
                label={
                  <span>
                    <span className="fw-semibold text-danger">Delete sub-orgs too</span>
                    <div className="text-muted small">
                      Soft-deletes {org.name} and all {descendantCount} descendant
                      {descendantCount === 1 ? "" : "s"}. Can be undone by a platform admin.
                    </div>
                  </span>
                }
              />
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant={hasChildren && mode === "cascade" ? "danger" : "danger"}
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading
            ? "Deleting…"
            : !hasChildren
            ? "Delete"
            : mode === "reparent"
            ? `Promote ${descendantCount} & delete`
            : `Delete with ${descendantCount} sub-org${descendantCount === 1 ? "" : "s"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrgDeleteModal;
